import { AlertMessage } from "@/components/AlertMessage";
import Button from "@/components/bricks/button/Button";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import { FormikDatePicker } from "@/components/formcontrols/DatePicker";
import IsRequired from "@/components/formcontrols/common/IsRequired";
import { SelectFormik } from "@/components/formcontrols/Select";
import FristForhaandsvisning from "./FristForhaandsvisning";
import { DokumentationGaelderFraFelt } from "./DokumentationGaelderFraFelt";
import {
  erEfterAfgoerelsesdato,
  foreslaaDokumentationGaelderFra,
  type EfteruddannelsesperiodeInterval,
} from "./dokumentationGaelderFra";
import PlusIcon from "@/components/icons/PlusIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import { UserQueryContext } from "@/context/UserContext";
import { useCreatePersonPaabud } from "@/hooks/person-hooks/usePersonPaabud";
import { dateToyyyyMMdd } from "@/util/date";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useEffect } from "react";
import * as Yup from "yup";
import {
  FRIST_LAENGDE_MAANEDER,
  PERIODE_LAENGDER,
  PERIODE_LAENGDE_OPTIONS,
} from "./injunctionPeriode";
import { YupNumberOfLessonsRequired } from "../../../../../util/yup";

interface CreatePaabudModalProps {
  personId: string;
  /** Advokatens efteruddannelsesperioder — bruges til at foreslå "Dokumentation gælder fra". */
  perioder: EfteruddannelsesperiodeInterval[];
}

interface CreatePaabudFormValues {
  numberOfLessons: string;
  kendelseDato: Date | null;
  dokumentationGaelderFra: Date | null;
  periodeLaengdeMaaneder: string;
  bemaerkning: string;
  file: File | null;
}

const initialValues: CreatePaabudFormValues = {
  numberOfLessons: "",
  kendelseDato: null,
  dokumentationGaelderFra: null,
  periodeLaengdeMaaneder: "",
  bemaerkning: "",
  file: null,
};

const validationSchema = Yup.object({
  numberOfLessons: YupNumberOfLessonsRequired(),
  kendelseDato: Yup.date().nullable().required("Afgørelsesdato skal udfyldes"),
  dokumentationGaelderFra: Yup.date()
    .nullable()
    .test(
      "ikkeEfterAfgoerelse",
      "Kan ikke ligge efter afgørelsesdatoen",
      (value, context) =>
        !erEfterAfgoerelsesdato(value ?? null, context.parent.kendelseDato),
    ),
  periodeLaengdeMaaneder: Yup.string()
    .required("Indfrielsesperiodens længde skal vælges")
    .oneOf([...PERIODE_LAENGDER], "Ugyldig længde"),
  file: Yup.mixed<File>()
    .nullable()
    .required("Afgørelse (PDF) skal uploades")
    .test("fileType", "Filen skal være en PDF", (value) => {
      if (!value) return false;

      return (
        value instanceof File &&
        (value.type === "application/pdf" ||
          value.name.toLowerCase().endsWith(".pdf"))
      );
    }),
});

const CreatePaabudForm = ({ personId, perioder }: CreatePaabudModalProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate,
    isPending,
    isError,
    reset: resetMutation,
  } = useCreatePersonPaabud();

  const formik = useFormik<CreatePaabudFormValues>({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (!values.kendelseDato || !values.file) {
        return;
      }

      mutate(
        {
          personId,
          file: values.file,
          body: {
            paakraevedeLektioner: Number(values.numberOfLessons),
            periodeLaengdeMaaneder: Number(values.periodeLaengdeMaaneder),
            kendelseDato: dateToyyyyMMdd(values.kendelseDato),
            dokumentationGaelderFra: values.dokumentationGaelderFra
              ? dateToyyyyMMdd(values.dokumentationGaelderFra)
              : null,
            bemaerkning: values.bemaerkning.trim() || null,
          },
        },
        {
          onSuccess: () => {
            formik.resetForm();
            resetMutation();
            setModalOpen(false);
          },
        },
      );
    },
  });

  // Forslaget følger afgørelsesdatoen, indtil sagsbehandleren selv har rørt feltet.
  const { kendelseDato } = formik.values;
  const dokumentationRoert = !!formik.touched.dokumentationGaelderFra;
  const { setFieldValue } = formik;
  useEffect(() => {
    if (dokumentationRoert) return;
    const forslag = foreslaaDokumentationGaelderFra(perioder, kendelseDato);
    void setFieldValue(
      "dokumentationGaelderFra",
      forslag ? new Date(forslag) : null,
      false,
    );
  }, [kendelseDato, dokumentationRoert, perioder, setFieldValue]);

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-item-list c-modal-container-inner">
            <div className="flex-100">
              <label className="form-label" htmlFor="numberOfLessons">
                Antal lektioner
                <IsRequired />
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.numberOfLessons &&
                  formik.errors.numberOfLessons
                    ? " is-invalid"
                    : ""
                }`}
                id="numberOfLessons"
                min="0"
                step="any"
                type="number"
                value={formik.values.numberOfLessons}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.numberOfLessons}
                errorMessage={formik.errors.numberOfLessons}
              />
            </div>
            <div className="flex-100">
              <FormikDatePicker
                formik={formik}
                config={{
                  component: "datepicker",
                  id: "kendelseDato",
                  label: "Afgørelsesdato",
                  initialValue: "",
                  isRequired: true,
                }}
              />
            </div>
            <div className="flex-100">
              <SelectFormik
                data={PERIODE_LAENGDE_OPTIONS}
                formik={formik}
                config={{
                  component: "select",
                  id: "periodeLaengdeMaaneder",
                  label: "Indfrielsesperiodens længde",
                  placeholder: "Vælg længde...",
                  isRequired: true,
                }}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="frist-laengde">
                Fristens længde
              </label>
              <input
                className="form-control"
                disabled
                id="frist-laengde"
                readOnly
                type="text"
                value={`${FRIST_LAENGDE_MAANEDER} måneder`}
              />
              <p className="text-muted small mb-0 mt-1">
                Fast for påbud. Regnes fra indfrielsesperiodens udløb.
              </p>
            </div>
            <div className="flex-100">
              <FristForhaandsvisning
                type="paabud"
                personId={personId}
                kendelseDato={
                  formik.values.kendelseDato
                    ? dateToyyyyMMdd(formik.values.kendelseDato)
                    : null
                }
                periodeLaengdeMaaneder={
                  formik.values.periodeLaengdeMaaneder
                    ? Number(formik.values.periodeLaengdeMaaneder)
                    : null
                }
              />
            </div>
            <div className="flex-100">
              <DokumentationGaelderFraFelt formik={formik} afgoerelse="påbud" />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="bemaerkning">
                Bemærkning
              </label>
              <textarea
                className="form-control"
                id="bemaerkning"
                rows={3}
                value={formik.values.bemaerkning}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="paabud-file">
                Afgørelse (PDF)
                <IsRequired />
              </label>
              <input
                accept="application/pdf,.pdf"
                className={`form-control${
                  formik.touched.file && formik.errors.file ? " is-invalid" : ""
                }`}
                id="paabud-file"
                type="file"
                onBlur={() => formik.setFieldTouched("file", true)}
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0] ?? null;
                  formik.setFieldValue("file", file, true);
                }}
              />
              {formik.values.file && (
                <p className="text-muted small mb-0 mt-1">
                  {formik.values.file.name}
                </p>
              )}
              <ValidationErrorMessage
                touched={formik.touched.file}
                errorMessage={formik.errors.file}
              />
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner mb-2">
              <AlertMessage type="danger" warningIcon heading="Fejl">
                <p className="mb-0">Kunne ikke oprette påbud.</p>
              </AlertMessage>
            </div>
          )}
          <StickyFooter>
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button color="primary" disabled={isPending} type="submit">
                Opret
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

export const CreatePaabudModal = ({
  personId,
  perioder,
}: CreatePaabudModalProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);

  if (!hasPermission(user, "TilsynWrite")) {
    return null;
  }

  return (
    <ModalProvider>
      <ModalTrigger
        asChild
        className="btn btn-primary btn--transparent has-icon px-2 justify-content-end"
      >
        <button id="efteruddannelse-opret-paabud" type="button">
          Opret påbud
          <span className="icon-bg badge">
            <PlusIcon />
          </span>
        </button>
      </ModalTrigger>
      <Modal title="Opret påbud">
        <CreatePaabudForm personId={personId} perioder={perioder} />
      </Modal>
    </ModalProvider>
  );
};
